/** Keep Entangle review-photo links crawlable and its close control reachable. */
(() => {
  'use strict';

  const widgetSelector = '.grp-entangle--root, #grp-entangle--root';
  const linkSelector = 'a.grp-multiple-link';
  const emptyLink = /^\s*javascript\s*:\s*void\s*\(\s*0\s*\)\s*;?\s*$/i;
  const lightboxHandler = /^\s*(?:return\s+)?(?:window\.)?grpHandleImageClick\s*\(/;
  const photoModalSelector = '#image-modal.rgr-modal';
  const closeHandler = /^\s*(?:return\s+)?(?:window\.)?grpCloseModal\s*\(\s*\)\s*;?\s*$/;
  const widgets = new WeakMap();
  const repairedLinks = new WeakMap();
  const keyboardCloseControls = new WeakSet();

  function imageUrl(link) {
    const image = link.querySelector('img.grp-multiple-image');
    const source = image && (image.getAttribute('src') || image.currentSrc);
    if (!source) return null;

    try {
      const url = new URL(source, document.baseURI);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
    } catch (_) {
      return null;
    }
  }

  function repairLink(link) {
    if (!link.matches(linkSelector) || !link.closest(widgetSelector)) return;

    const href = link.getAttribute('href') || '';
    const previousUrl = repairedLinks.get(link);
    // Leave genuine vendor links alone, including any future app-side fix.
    if (!emptyLink.test(href) && href !== previousUrl) return;
    if (!lightboxHandler.test(link.getAttribute('onclick') || '')) return;

    const url = imageUrl(link);
    if (!url) return;

    if (!previousUrl) {
      link.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
            event.ctrlKey || event.shiftKey || event.altKey) return;
        if (repairedLinks.get(link) !== link.getAttribute('href')) return;

        // The original inline handler runs first; only suppress navigation
        // while that handler and its vendor function are still available.
        if (lightboxHandler.test(link.getAttribute('onclick') || '') &&
            typeof window.grpHandleImageClick === 'function') {
          event.preventDefault();
        }
      });
    }

    repairedLinks.set(link, url);
    if (href !== url) link.setAttribute('href', url);
  }

  function collectLinks(node, links) {
    if (node.nodeType !== 1) return;
    const parentLink = node.closest(linkSelector);
    if (parentLink) links.add(parentLink);
    node.querySelectorAll(linkSelector).forEach((link) => links.add(link));
  }

  function watchWidget(widget) {
    if (widgets.has(widget)) return;

    const observer = new MutationObserver((records) => {
      const links = new Set();
      records.forEach((record) => {
        if (record.type === 'attributes') {
          collectLinks(record.target, links);
        } else {
          record.addedNodes.forEach((node) => collectLinks(node, links));
        }
      });
      links.forEach(repairLink);
    });

    widgets.set(widget, observer);
    observer.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'src', 'onclick']
    });
    widget.querySelectorAll(linkSelector).forEach(repairLink);
  }

  function visitWidgets(node, callback) {
    if (node.nodeType !== 1) return;
    if (node.matches(widgetSelector)) callback(node);
    node.querySelectorAll(widgetSelector).forEach(callback);
  }

  function photoModalClose(modal) {
    if (!modal || !modal.matches(photoModalSelector)) return null;
    const close = modal.querySelector(':scope > .entangle-grp-modal-close');
    if (!close || !closeHandler.test(close.getAttribute('onclick') || '') ||
        !modal.querySelector('.rgr-modal-content #myRgrSilder')) return null;
    return close;
  }

  function photoModalIsVisible(modal) {
    if (!modal.isConnected || modal.hidden) return false;
    const style = window.getComputedStyle(modal);
    return style.display !== 'none' && style.visibility !== 'hidden' &&
      style.visibility !== 'collapse' && style.opacity !== '0';
  }

  function repairPhotoModal(modal) {
    const close = photoModalClose(modal);
    if (!close) return;

    // The vendor's later positioned slideshow can cover the mobile X.
    // Give only this recognised close control a layer; keep vendor handlers.
    if (!close.style.zIndex) {
      const layer = window.getComputedStyle(close).zIndex;
      if (!layer || layer === 'auto') close.style.zIndex = '1';
    }

    if (!(close.getAttribute('aria-label') || '').trim()) {
      close.setAttribute('aria-label', 'Close review photo');
    }
    // A future native button already supplies its own Enter/Space activation.
    if (close.tagName === 'BUTTON' || close.tagName === 'INPUT') return;
    if (!close.hasAttribute('role')) close.setAttribute('role', 'button');
    if (!close.hasAttribute('tabindex')) close.setAttribute('tabindex', '0');
    if (keyboardCloseControls.has(close)) return;
    keyboardCloseControls.add(close);
    close.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || !photoModalIsVisible(modal) ||
          (event.key !== 'Enter' && event.key !== ' ')) return;
      if (photoModalClose(modal) !== close) return;
      event.preventDefault();
      close.click();
    });
  }

  function closePhotoOnEscape(event) {
    if (event.defaultPrevented || event.key !== 'Escape') return;
    const modal = document.querySelector(photoModalSelector);
    const close = photoModalClose(modal);
    if (!close || !photoModalIsVisible(modal)) return;
    event.preventDefault();
    close.click();
  }

  function visitPhotoModals(node) {
    if (node.nodeType !== 1) return;
    const modal = node.closest(photoModalSelector);
    if (modal) repairPhotoModal(modal);
    node.querySelectorAll(photoModalSelector).forEach(repairPhotoModal);
  }

  function start() {
    document.querySelectorAll(widgetSelector).forEach(watchWidget);
    document.querySelectorAll(photoModalSelector).forEach(repairPhotoModal);
    document.addEventListener('keydown', closePhotoOnEscape);

    // Discover section/app replacements without observing page-wide attributes.
    const discovery = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach(visitPhotoModals);
        if (record.target.nodeType === 1 && record.target.closest(widgetSelector)) return;
        record.addedNodes.forEach((node) => visitWidgets(node, watchWidget));
        record.removedNodes.forEach((node) => visitWidgets(node, (widget) => {
          if (!widget.isConnected && widgets.has(widget)) {
            widgets.get(widget).disconnect();
            widgets.delete(widget);
          }
        }));
      });
    });
    discovery.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
